'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MenuContext } from '@srcDS/provider/menu/MenuContext';
import { shallowEqual, useSelector } from 'react-redux';

export const MenuProvider = ({ children }) => {
  const [mobileMenuLevels, setMobileMenuLevels] = useState([]);
  const [currentLevelName, setCurrentLevelName] = useState(null);
  const [shownMenu, setShownMenu] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  const [currentParentCategory, setCurrentParentCategory] = useState(null);
  const [activeLevel1, setActiveLevel1] = useState(null); // New state
  const [selectedLevel2, setSelectedLevel2] = useState(null); // New state
  const hasFirstCategoryBeenSet = useRef(false);

  const categories = useMemo(() => activeLevel1?.tree?.children ?? [], [activeLevel1]);
  const hasCategories = categories.length > 0;

  const { locale } = useSelector(
    (state) => ({
      nodeId: state.node.currentNodeId || null,
      locale: state.app.context.locale || {},
    }),
    shallowEqual,
  );
  const currentLocale = locale === 'en_AU@NZD' ? 'NZ' : 'AU';

  useEffect(() => {
    const wrapper = document.getElementById('bodyWrapper');
    if (wrapper) {
      wrapper.style.zIndex = shownMenu ? '100' : '1';
    }
  }, [shownMenu]);

  const getVisibleCategories = (categories, isLoggedIn) =>
    categories.filter(({ devicePreference, displayRegion, showOnlyForLoggedInUsers }) => {
      const validDevice = devicePreference === 'all' || devicePreference === 'desktop';
      const validRegion = displayRegion === 'all' || displayRegion === currentLocale;
      const validUser = showOnlyForLoggedInUsers ? isLoggedIn : true;
      return validDevice && validRegion && validUser;
    });

  const getMenuItemData = (category) => {
    const isTop = !category?.depth;
    const level = !category?.depth && category?.tree ? 1 : category?.depth;
    const children = level === 1 ? category?.tree?.children : category?.children;
    const hasChildren = children && !!children?.length && children.some((c) => !c?.configuration?.hideInMenu);

    const { configuration, tree } = category;
    const isVisibleInCurrentLocale =
      typeof configuration?.[`show${currentLocale}`] !== 'boolean' || configuration?.[`show${currentLocale}`];
    const navItemColor = configuration?.navItemColor || tree?.configuration?.navItemColor;
    const isVisibleInMenu = configuration ? !configuration?.hideInMenu : !tree?.configuration?.hideInMenu;

    return {
      isTop,
      level,
      children,
      hasChildren,
      configuration,
      tree,
      isVisibleInCurrentLocale,
      navItemColor,
      isVisibleInMenu,
    };
  };

  useEffect(() => {
    if (!mobileMenuLevels || mobileMenuLevels.length === 0) {
      setCurrentLevelName(null);
    }
  }, [mobileMenuLevels]);

  const firstLevel2WithLevel3 = useMemo(() => {
    if (!hasCategories) return null;
    return (
      categories.find((category) => {
        const { children, isVisibleInCurrentLocale, isVisibleInMenu } = getMenuItemData(category);
        return isVisibleInCurrentLocale && isVisibleInMenu && children?.length > 0;
      }) ||
      categories[0] ||
      null
    );
  }, [categories, activeLevel1, currentLevelName]);

  const setDefaultLevelNames = useCallback(
    (category = null) => {
      const shouldSet = hasCategories && firstLevel2WithLevel3 && (category || !selectedLevel2);

      if (shouldSet) {
        let newSelectedLevel2 = firstLevel2WithLevel3.name;
        if (category) {
          const newDefaultCategory = category?.tree?.children.find((c) => {
            const { children, isVisibleInCurrentLocale, isVisibleInMenu } = getMenuItemData(c);
            return isVisibleInCurrentLocale && isVisibleInMenu && children?.length > 0;
          });
          newSelectedLevel2 = newDefaultCategory?.name || firstLevel2WithLevel3?.name;
        }

        setSelectedLevel2(newSelectedLevel2);
        setCurrentLevelName(newSelectedLevel2);
      }
    },
    [hasCategories, firstLevel2WithLevel3, selectedLevel2, setSelectedLevel2, setCurrentLevelName],
  );

  useEffect(() => {
    setDefaultLevelNames();
  }, [activeLevel1, hasCategories, firstLevel2WithLevel3, setSelectedLevel2, setCurrentLevelName]);

  const closeMobileMenuOnClick = () => setCurrentLevelName(null);
  const goBackToPrevLevel = () => setCurrentLevelName(mobileMenuLevels[mobileMenuLevels.length - 1]);

  const values = useMemo(
    () => ({
      currentParentCategory,
      setCurrentParentCategory,
      currentLevelName,
      setCurrentLevelName,
      isTouch,
      setIsTouch,
      shownMenu,
      setShownMenu,
      getMenuItemData,
      getVisibleCategories,
      currentLocale,
      mobileMenuLevels,
      setMobileMenuLevels,
      closeMobileMenuOnClick,
      goBackToPrevLevel,
      hasFirstCategoryBeenSet,
      activeLevel1,
      setActiveLevel1,
      selectedLevel2,
      setSelectedLevel2,
      categories,
      hasCategories,
      firstLevel2WithLevel3,
      setDefaultLevelNames,
    }),
    [
      currentParentCategory,
      currentLevelName,
      isTouch,
      shownMenu,
      currentLocale,
      mobileMenuLevels,
      activeLevel1,
      selectedLevel2,
      categories,
      hasCategories,
      firstLevel2WithLevel3,
    ],
  );

  return <MenuContext.Provider value={values}>{children}</MenuContext.Provider>;
};
